import { groupOpportunityRepo, opportunityParticipantRepo } from "@repositories";
import { HyperBackendMethod } from "@shared/backend/hyper-backend-method";
import { Controller } from "remult";
import { sendTemplatedUserNotification } from "@shared/notifications/templated-user-notification";
import { NotificationTemplateKey } from "@config/notification-templates.config";
import { WEME_APP_BASE_URL } from "@shared/helpers/constants";
import { buildFeedPath } from "@shared/notifications/notification-navigation";
import { syncOpportunityFullFlagFromSeats } from "@shared/helpers/sync-opportunity-full-flag";

@Controller("opportunity-participant-methods")
export class OpportunityParticipantMethods {
  @HyperBackendMethod({
    apiPrefix: "opportunity-participant-methods",
    allowed: true,
    overrideSecurity: true,
  })
  static async approveOrDeclineVolunteer(params: {
    participantId: string;
    isApproved: boolean;
  }) {
    const { participantId, isApproved } = params;

    const participant = await opportunityParticipantRepo.findOne({
      where: { id: participantId },
      include: {
        association: {
          include: {
            user: true,
          },
        },
        opportunity: true,
        group: true,
      },
    });

    if (!participant || !participant.association?.user) {
      throw new Error("Participant or user not found");
    }

    const opportunityId = participant.opportunityId;
    const wasAlreadyApproved = participant.status === "APPROVED";

    if (
      isApproved &&
      participant.groupId &&
      opportunityId &&
      !wasAlreadyApproved
    ) {
      const existingGroupOpportunity = await groupOpportunityRepo.findOne({
        where: {
          groupId: participant.groupId,
          opportunityId,
        },
      });
      if (!existingGroupOpportunity) {
        await groupOpportunityRepo.insert({
          groupId: participant.groupId,
          opportunityId,
        });
      }
    }

    if (!isApproved && participant.groupId && opportunityId) {
      await groupOpportunityRepo.deleteMany({
        where: {
          groupId: participant.groupId,
          opportunityId,
        },
      });
    }

    // Update status
    await opportunityParticipantRepo.update(participantId, {
      status: isApproved ? "APPROVED" : "DECLINED",
    });

    if (participant.opportunity?.id) {
      await syncOpportunityFullFlagFromSeats(
        participant.opportunity,
        !isApproved,
      );
    }

    // Notify volunteer if declined (push + SMS + in-app in parallel)
    const volunteerUser = participant.association.user;
    if (!isApproved && (volunteerUser.id || volunteerUser.phoneNumber)) {
      const feedLink = `${WEME_APP_BASE_URL}${buildFeedPath()}`;
      await sendTemplatedUserNotification({
        templateKey: NotificationTemplateKey.SEEKER_DECLINED_VOLUNTEER,
        placeholders: {
          user_name: volunteerUser.name || "",
          feed_link: feedLink,
        },
        userId: volunteerUser.id ?? undefined,
        phoneNumber: volunteerUser.phoneNumber ?? undefined,
        channels: {
          push: !!volunteerUser.id,
          sms: !!volunteerUser.phoneNumber,
          inApp: !!volunteerUser.id,
        },
        navigateTo: buildFeedPath(),
      });
    }

    return { success: true };
  }
}
